import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaUser, FaEnvelope, FaCalendar, FaMapMarkerAlt, 
  FaTransgender, FaHeart, FaStar, FaCamera,
  FaEdit, FaSave, FaTimes, FaSpinner, FaLock,
  FaLockOpen, FaUpload, FaTrash, FaCheck,
  FaChevronLeft, FaChevronRight, FaImages,
  FaVideo, FaUsers, FaEye, FaComments, FaPlay,
  FaComment, FaFilter, FaSyncAlt, FaPaperPlane
} from 'react-icons/fa';
import { Plyr } from 'plyr-react';
import 'plyr-react/plyr.css';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import uploadService from '../../services/upload';
import videoService from '../../services/video';
import toast from 'react-hot-toast';
import Loader from '../common/Loader';
import Modal from '../common/Modal';
import { useBoxview } from '../../hooks/useBoxview';
import Boxview from '../common/Boxview';
import './profile.css';

const MyProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState('infos');
  
  // States pour les médias
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  
  // States pour la suppression
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // Stats
  const [stats, setStats] = useState({
    photos: 0,
    videos: 0,
    matches: 0,
    views: 0,
    likes: 0
  });

  // Form data pour l'édition
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    pseudo: '',
    bio: '',
    ville: '',
    pays: '',
    genre: '',
    orientation: '',
    date_naissance: '',
    origine: ''
  });

  // Upload avatars
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const avatarInputRef = useRef(null);
  const coverInputRef = useRef(null);

  // Upload médias
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadType, setUploadType] = useState('photos'); // 'photos' ou 'videos'
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadData, setUploadData] = useState({
    titre: '',
    description: '',
    file: null,
    isPrivate: false
  });
  const fileInputRef = useRef(null);

  // Galerie
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [galleryType, setGalleryType] = useState('photos');
  const [galleryItems, setGalleryItems] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(false);
  
  // Refs pour Plyr
  const plyrRefs = useRef({});

  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const boxview = useBoxview();

  const UPLOADS_URL = process.env.REACT_APP_UPLOADS_URL || 'https://murima.julesferry-ga.com/uploads';

  // Charger le profil
  const loadProfile = useCallback(async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const response = await api.get('/get-profile.php', {
        params: { 
          profile_id: user.id,
          current_user_id: user.id
        }
      });

      if (response.data.success) {
        const userData = response.data.user;
        
        const fullProfile = {
          ...userData,
          photos: response.data.photos || [],
          videos: response.data.videos || [],
          interests: response.data.interests || []
        };
        
        setProfile(fullProfile);
        
        setFormData({
          prenom: userData.prenom || '',
          nom: userData.nom || '',
          pseudo: userData.pseudo || '',
          bio: userData.bio || '',
          ville: userData.ville || '',
          pays: userData.pays || '',
          genre: userData.genre || 'autre',
          orientation: userData.orientation || 'autre',
          date_naissance: userData.date_naissance || '',
          origine: userData.origine || ''
        });

        setStats({
          photos: response.data.photos?.length || 0,
          videos: response.data.videos?.length || 0,
          matches: 0,
          views: userData.total_vues || 0,
          likes: userData.total_likes || 0
        });
      }
    } catch (error) {
      console.error('❌ Error loading profile:', error);
      toast.error('Erreur lors du chargement du profil');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // Sauvegarder les modifications
  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.post('/update-profile.php', {
        user_id: user.id,
        ...formData
      });

      if (response.data.success) {
        toast.success('Profil mis à jour avec succès');
        setEditMode(false);
        loadProfile();
      } else {
        toast.error(response.data.error || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('❌ Error saving profile:', error);
      toast.error('Erreur réseau');
    } finally {
      setSaving(false);
    }
  };

  // Annuler les modifications
  const handleCancel = () => {
    if (profile) {
      setFormData({
        prenom: profile.prenom || '',
        nom: profile.nom || '',
        pseudo: profile.pseudo || '',
        bio: profile.bio || '',
        ville: profile.ville || '',
        pays: profile.pays || '',
        genre: profile.genre || 'autre',
        orientation: profile.orientation || 'autre',
        date_naissance: profile.date_naissance || '',
        origine: profile.origine || ''
      });
    }
    setEditMode(false);
  };

  // Upload avatar
  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez JPG, PNG, GIF ou WEBP');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 5MB');
      return;
    }

    setUploadingAvatar(true);
    try {
      const result = await uploadService.uploadPhoto(file, 'avatar');

      if (result.success) {
        updateUser({ avatar: result.url });
        toast.success('Photo de profil mise à jour');
        loadProfile();
      } else {
        toast.error(result.error || 'Erreur lors de l\'upload');
      }
    } catch (error) {
      console.error('❌ Avatar upload error:', error);
      toast.error('Erreur réseau');
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  // Upload cover
  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez JPG, PNG, GIF ou WEBP');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 10MB');
      return;
    }

    setUploadingCover(true);
    try {
      const result = await uploadService.uploadPhoto(file, 'cover');

      if (result.success) {
        updateUser({ cover_photo: result.url });
        toast.success('Photo de couverture mise à jour');
        loadProfile();
      } else {
        toast.error(result.error || 'Erreur lors de l\'upload');
      }
    } catch (error) {
      console.error('❌ Cover upload error:', error);
      toast.error('Erreur réseau');
    } finally {
      setUploadingCover(false);
      if (coverInputRef.current) coverInputRef.current.value = '';
    }
  };

  // Upload média (photo ou vidéo)
  const openUploadModal = (type) => {
    setUploadType(type);
    setUploadData({
      titre: '',
      description: '',
      file: null,
      isPrivate: false
    });
    setUploadProgress(0);
    setShowUploadModal(true);
  };

  const handleUploadChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      const file = files[0];
      if (file) {
        if (uploadType === 'videos') {
          const validTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo'];
          if (!validTypes.includes(file.type)) {
            toast.error('Format non supporté. Utilisez MP4, MOV ou AVI');
            e.target.value = '';
            return;
          }
          if (file.size > 100 * 1024 * 1024) {
            toast.error('La vidéo ne doit pas dépasser 100MB');
            e.target.value = '';
            return;
          }
        } else {
          const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
          if (!validTypes.includes(file.type)) {
            toast.error('Format non supporté. Utilisez JPG, PNG, GIF ou WEBP');
            e.target.value = '';
            return;
          }
          if (file.size > 10 * 1024 * 1024) {
            toast.error('L\'image ne doit pas dépasser 10MB');
            e.target.value = '';
            return;
          }
        }
        setUploadData(prev => ({ ...prev, file }));
      }
    } else if (type === 'checkbox') {
      setUploadData(prev => ({ ...prev, [name]: checked }));
    } else {
      setUploadData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadData.file) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }

    if (uploadType === 'videos' && !uploadData.titre.trim()) {
      toast.error('Veuillez saisir un titre');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      let result;
      if (uploadType === 'videos') {
        result = await uploadService.uploadVideo(
          uploadData.file,
          uploadData.titre,
          uploadData.isPrivate,
          (progress) => setUploadProgress(progress)
        );
      } else {
        result = await uploadService.uploadPhoto(
          uploadData.file,
          'photo',
          uploadData.isPrivate,
          (progress) => setUploadProgress(progress)
        );
      }

      if (result.success) {
        toast.success(`${uploadType === 'videos' ? 'Vidéo' : 'Photo'} publiée avec succès !`);
        setShowUploadModal(false);
        resetUploadForm();
        loadProfile();
      } else {
        toast.error(result.error || `Erreur lors de l'upload`);
      }
    } catch (error) {
      console.error('❌ Upload error:', error);
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const resetUploadForm = () => {
    setUploadData({
      titre: '',
      description: '',
      file: null,
      isPrivate: false
    });
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Charger la galerie
  const loadGallery = async (type) => {
    setGalleryType(type);
    setLoadingGallery(true);
    
    try {
      const endpoint = type === 'photos' ? '/get-user-photos.php' : '/get-user-videos.php';
      const response = await api.get(endpoint, {
        params: { 
          user_id: user.id,
          current_user_id: user.id
        }
      });

      if (response.data.success) {
        setGalleryItems(response.data.items || []);
        setShowGalleryModal(true);
      } else {
        toast.error(response.data.error || `Erreur lors du chargement des ${type}`);
      }
    } catch (error) {
      console.error(`❌ Error loading ${type}:`, error);
      toast.error(`Erreur réseau lors du chargement des ${type}`);
    } finally {
      setLoadingGallery(false);
    }
  };

  // Suppression
  const confirmDeleteItem = (itemId, type, e) => {
    e?.stopPropagation();
    setItemToDelete({ id: itemId, type });
    setShowDeleteConfirm(true);
  };

  const handleDeleteItem = async () => {
    if (!itemToDelete) return;

    try {
      const endpoint = itemToDelete.type === 'photos' ? '/delete-photo.php' : '/delete-video.php';
      const idField = itemToDelete.type === 'photos' ? 'photo_id' : 'video_id';
      
      const response = await api.post(endpoint, {
        [idField]: itemToDelete.id,
        user_id: user.id
      });

      if (response.data.success) {
        if (itemToDelete.type === 'photos') {
          setProfile({
            ...profile,
            photos: profile.photos.filter(p => p.id !== itemToDelete.id)
          });
          setStats({ ...stats, photos: stats.photos - 1 });
        } else {
          setProfile({
            ...profile,
            videos: profile.videos.filter(v => v.id !== itemToDelete.id)
          });
          setStats({ ...stats, videos: stats.videos - 1 });
        }
        
        toast.success(`${itemToDelete.type === 'photos' ? 'Photo' : 'Vidéo'} supprimée`);
        
        if (selectedVideo?.id === itemToDelete.id) {
          setShowVideoModal(false);
          setSelectedVideo(null);
        }
        if (selectedPhoto?.id === itemToDelete.id) {
          setShowPhotoModal(false);
          setSelectedPhoto(null);
        }
      }
    } catch (error) {
      console.error('❌ Delete error:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setShowDeleteConfirm(false);
      setItemToDelete(null);
    }
  };

  // Like pour les vidéos
  const handleLikeVideo = async (videoId, e) => {
    e.stopPropagation();
    
    if (!user?.id) {
      toast.error('Connectez-vous pour liker');
      return;
    }

    const videoIndex = profile.videos.findIndex(v => v.id === videoId);
    if (videoIndex === -1) return;

    const wasLiked = profile.videos[videoIndex].user_liked;
    
    const updatedVideos = [...profile.videos];
    updatedVideos[videoIndex] = {
      ...updatedVideos[videoIndex],
      likes: wasLiked ? updatedVideos[videoIndex].likes - 1 : updatedVideos[videoIndex].likes + 1,
      user_liked: !wasLiked
    };
    
    setProfile({ ...profile, videos: updatedVideos });

    try {
      const response = await api.post('/like-video.php', {
        video_id: videoId,
        user_id: user.id
      });

      if (!response.data.success) {
        updatedVideos[videoIndex] = {
          ...updatedVideos[videoIndex],
          likes: profile.videos[videoIndex].likes,
          user_liked: wasLiked
        };
        setProfile({ ...profile, videos: updatedVideos });
        toast.error('Erreur lors du like');
      }
    } catch (error) {
      updatedVideos[videoIndex] = {
        ...updatedVideos[videoIndex],
        likes: profile.videos[videoIndex].likes,
        user_liked: wasLiked
      };
      setProfile({ ...profile, videos: updatedVideos });
      toast.error('Erreur réseau');
    }
  };

  // Lecture vidéo
  const playVideo = (video) => {
    setSelectedVideo(video);
    setShowVideoModal(true);
  };

  // Formateurs
  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
    return num.toString();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diff === 0) return "Aujourd'hui";
    if (diff === 1) return 'Hier';
    if (diff < 7) return `Il y a ${diff} jours`;
    if (diff < 30) return `Il y a ${Math.floor(diff / 7)} semaines`;
    return date.toLocaleDateString('fr-FR');
  };

  const getAvatarUrl = (avatar) => {
    if (!avatar) return 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e';
    if (avatar.startsWith('http')) return avatar;
    return `${UPLOADS_URL}/${avatar}`;
  };

  const getCoverUrl = (cover) => {
    if (!cover) return 'https://images.unsplash.com/photo-1519681393784-d120267933ba';
    if (cover.startsWith('http')) return cover;
    return `${UPLOADS_URL}/${cover}`;
  };

  const getVideoUrl = (video) => {
    if (!video?.url) return '';
    if (video.url.startsWith('http')) return video.url;
    return `${UPLOADS_URL}/${video.url}`;
  };

  const getThumbnailUrl = (video) => {
    if (video?.thumbnail) {
      return video.thumbnail.startsWith('http') 
        ? video.thumbnail 
        : `${UPLOADS_URL}/${video.url}`;
    }
    return 'https://images.unsplash.com/photo-1574375927938-d5a98e8ffe85';
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="profile-page">
      {/* Cover Photo */}
      <div className="profile-cover">
        <img 
          src={getCoverUrl(profile?.cover_photo)} 
          alt="Cover" 
          className="cover-image"
        />
        
        <div className="cover-actions">
          <input
            type="file"
            ref={coverInputRef}
            onChange={handleCoverUpload}
            accept="image/jpeg,image/png,image/gif,image/webp"
            style={{ display: 'none' }}
          />
          <button 
            className="btn-change-cover"
            onClick={() => coverInputRef.current?.click()}
            disabled={uploadingCover}
          >
            {uploadingCover ? <FaSpinner className="spinner" /> : <FaCamera />}
          </button>
        </div>

        {/* Avatar */}
        <div className="profile-avatar-container">
          <div className="profile-avatar">
            <img 
              src={getAvatarUrl(profile?.avatar)} 
              alt={profile?.pseudo} 
            />
            <div className="avatar-overlay">
              <input
                type="file"
                ref={avatarInputRef}
                onChange={handleAvatarUpload}
                accept="image/jpeg,image/png,image/gif,image/webp"
                style={{ display: 'none' }}
              />
              <button 
                className="btn-change-avatar"
                onClick={() => avatarInputRef.current?.click()}
                disabled={uploadingAvatar}
              >
                {uploadingAvatar ? <FaSpinner className="spinner" /> : <FaCamera />}
              </button>
            </div>
          </div>

          <div className="profile-title">
            <h1>
              {profile?.pseudo}
              {profile?.is_verified && (
                <span className="verified-badge" title="Profil vérifié">
                  <FaCheck />
                </span>
              )}
              {profile?.is_premium && (
                <span className="premium-badge" title="Membre premium">
                  <FaStar />
                </span>
              )}
            </h1>
            <p className="profile-status">
              {profile?.en_ligne ? (
                <span className="online"><span className="dot"></span> En ligne</span>
              ) : (
                <span className="offline"><span className="dot"></span> Hors ligne</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Actions rapides */}
      <div className="profile-actions">
        <button className="action-btn edit-btn" onClick={() => setEditMode(true)}>
          <FaEdit /> Modifier profil
        </button>
        <button className="action-btn" onClick={() => openUploadModal('photos')}>
          <FaUpload /> Publier photo
        </button>
        <button className="action-btn" onClick={() => openUploadModal('videos')}>
          <FaVideo /> Publier vidéo
        </button>
      </div>

      {/* Navigation tabs */}
      <div className="profile-tabs">
        <button 
          className={`tab-btn ${activeTab === 'infos' ? 'active' : ''}`}
          onClick={() => setActiveTab('infos')}
        >
          <FaUser /> Infos
        </button>
        <button 
          className={`tab-btn ${activeTab === 'photos' ? 'active' : ''}`}
          onClick={() => setActiveTab('photos')}
        >
          <FaImages /> Photos ({stats.photos})
        </button>
        <button 
          className={`tab-btn ${activeTab === 'videos' ? 'active' : ''}`}
          onClick={() => setActiveTab('videos')}
        >
          <FaVideo /> Vidéos ({stats.videos})
        </button>
       
      </div>

      {/* Contenu */}
      <div className="profile-content">
        {/* Onglet Informations */}
        {activeTab === 'infos' && (
          <div className="info-tab">
            <div className="stats-cards">
              <div className="stat-card">
                <FaEye className="stat-icon" />
                <div>
                  <span className="stat-value">{stats.views}</span>
                  <span className="stat-label">Vues du profil</span>
                </div>
              </div>
              <div className="stat-card">
                <FaHeart className="stat-icon" />
                <div>
                  <span className="stat-value">{stats.likes}</span>
                  <span className="stat-label">Likes reçus</span>
                </div>
              </div>
              <div className="stat-card">
                <FaImages className="stat-icon" />
                <div>
                  <span className="stat-value">{stats.photos}</span>
                  <span className="stat-label">Photos</span>
                </div>
              </div>
              <div className="stat-card">
                <FaVideo className="stat-icon" />
                <div>
                  <span className="stat-value">{stats.videos}</span>
                  <span className="stat-label">Vidéos</span>
                </div>
              </div>
            </div>

            <div className="profile-form-container">
              <div className="form-header">
                <h2>Informations personnelles</h2>
                {!editMode ? (
                  <button className="btn-edit" onClick={() => setEditMode(true)}>
                    <FaEdit /> Modifier
                  </button>
                ) : (
                  <div className="form-actions">
                    <button 
                      className="btn-save" 
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? <FaSpinner className="spinner" /> : <FaSave />}
                      Sauvegarder
                    </button>
                    <button 
                      className="btn-cancel-edit" 
                      onClick={handleCancel}
                      disabled={saving}
                    >
                      <FaTimes /> Annuler
                    </button>
                  </div>
                )}
              </div>

              <form className="profile-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Prénom</label>
                    {editMode ? (
                      <input
                        type="text"
                        value={formData.prenom}
                        onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                        placeholder="Votre prénom"
                      />
                    ) : (
                      <p className="field-value">{profile?.prenom || 'Non renseigné'}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Nom</label>
                    {editMode ? (
                      <input
                        type="text"
                        value={formData.nom}
                        onChange={(e) => setFormData({...formData, nom: e.target.value})}
                        placeholder="Votre nom"
                      />
                    ) : (
                      <p className="field-value">{profile?.nom || 'Non renseigné'}</p>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Pseudo</label>
                    {editMode ? (
                      <input
                        type="text"
                        value={formData.pseudo}
                        onChange={(e) => setFormData({...formData, pseudo: e.target.value})}
                        placeholder="Pseudo"
                        required
                      />
                    ) : (
                      <p className="field-value">{profile?.pseudo}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Email</label>
                    <p className="field-value">{profile?.email}</p>
                  </div>
                </div>

                <div className="form-group full">
                  <label>Bio</label>
                  {editMode ? (
                    <textarea
                      value={formData.bio}
                      onChange={(e) => setFormData({...formData, bio: e.target.value})}
                      placeholder="Parlez-nous de vous..."
                      rows="4"
                    />
                  ) : (
                    <p className="field-value bio">{profile?.bio || 'Aucune bio'}</p>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Genre</label>
                    {editMode ? (
                      <select
                        value={formData.genre}
                        onChange={(e) => setFormData({...formData, genre: e.target.value})}
                      >
                        <option value="homme">Homme</option>
                        <option value="femme">Femme</option>
                        <option value="non-binaire">Non-binaire</option>
                        <option value="autre">Autre</option>
                      </select>
                    ) : (
                      <p className="field-value capitalize">{profile?.genre || 'Non renseigné'}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Orientation</label>
                    {editMode ? (
                      <select
                        value={formData.orientation}
                        onChange={(e) => setFormData({...formData, orientation: e.target.value})}
                      >
                        <option value="hetero">Hétérosexuel</option>
                        <option value="homo">Homosexuel</option>
                        <option value="bi">Bisexuel</option>
                        <option value="autre">Autre</option>
                      </select>
                    ) : (
                      <p className="field-value capitalize">{profile?.orientation || 'Non renseigné'}</p>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Date de naissance</label>
                    {editMode ? (
                      <input
                        type="date"
                        value={formData.date_naissance}
                        onChange={(e) => setFormData({...formData, date_naissance: e.target.value})}
                      />
                    ) : (
                      <p className="field-value">
                        {formatDate(profile?.date_naissance)}
                        {profile?.age && (
                          <span className="age"> ({profile.age} ans)</span>
                        )}
                      </p>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Origine</label>
                    {editMode ? (
                      <input
                        type="text"
                        value={formData.origine}
                        onChange={(e) => setFormData({...formData, origine: e.target.value})}
                        placeholder="Ex: Française, Africaine..."
                      />
                    ) : (
                      <p className="field-value">{profile?.origine || 'Non renseigné'}</p>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Ville</label>
                    {editMode ? (
                      <input
                        type="text"
                        value={formData.ville}
                        onChange={(e) => setFormData({...formData, ville: e.target.value})}
                        placeholder="Votre ville"
                      />
                    ) : (
                      <p className="field-value">{profile?.ville || 'Non renseigné'}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Pays</label>
                    {editMode ? (
                      <input
                        type="text"
                        value={formData.pays}
                        onChange={(e) => setFormData({...formData, pays: e.target.value})}
                        placeholder="Votre pays"
                      />
                    ) : (
                      <p className="field-value">{profile?.pays || 'Non renseigné'}</p>
                    )}
                  </div>
                </div>

                {profile?.date_inscription && (
                  <div className="member-since">
                    <FaCalendar />
                    <span>Membre depuis le {new Date(profile.date_inscription).toLocaleDateString('fr-FR')}</span>
                  </div>
                )}
              </form>
            </div>
          </div>
        )}

        {/* Onglet Photos */}
        {activeTab === 'photos' && (
          <div className="gallery-tab">
            <div className="gallery-header">
              <h2>Mes photos</h2>
              <button 
                className="btn-view-all"
                onClick={() => loadGallery('photos')}
              >
                <FaImages /> Voir toutes mes photos
              </button>
            </div>

            <div className="photos-grid">
              {profile?.photos && profile.photos.length > 0 ? (
                profile.photos.slice(0, 6).map(photo => (
                  <div 
                    key={photo.id} 
                    className="photo-item"
                    onClick={() => {
                      const images = profile.photos.map(p => ({
                        url: `${UPLOADS_URL}/${p.url}`,
                        caption: 'Photo'
                      }));
                      const index = profile.photos.findIndex(p => p.id === photo.id);
                      boxview.openBoxview(images, index);
                    }}
                  >
                    <img 
                      src={`${UPLOADS_URL}/${photo.url}`} 
                      alt=""
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e';
                      }}
                    />
                    {photo.est_privee && (
                      <span className="private-icon"><FaLock /></span>
                    )}
                    {photo.est_principale && (
                      <span className="main-badge" title="Photo principale">⭐</span>
                    )}
                    <button 
                      className="btn-delete-item"
                      onClick={(e) => confirmDeleteItem(photo.id, 'photos', e)}
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))
              ) : (
                <p className="no-items">Aucune photo pour le moment</p>
              )}
            </div>
          </div>
        )}

        {/* Onglet Vidéos avec Plyr */}
        {activeTab === 'videos' && (
          <div className="gallery-tab">
            <div className="gallery-header">
              <h2>Mes vidéos</h2>
              <button 
                className="btn-view-all"
                onClick={() => loadGallery('videos')}
              >
                <FaVideo /> Voir toutes mes vidéos
              </button>
            </div>

            <div className="videos-grid">
              {profile?.videos && profile.videos.length > 0 ? (
                profile.videos.slice(0, 6).map(video => (
                  <div key={video.id} className="video-card">
                    <div className="video-thumbnail">
                      {/* Plyr dans la grille */}
                      <Plyr
                        ref={el => {
                          if (el) plyrRefs.current[video.id] = el;
                        }}
                        source={{
                          type: 'video',
                          sources: [
                            {
                              src: getVideoUrl(video),
                              type: 'video/mp4',
                            }
                          ]
                        }}
                        options={{
                          controls: ['play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen'],
                          autoplay: false,
                          muted: false,
                          volume: 1,
                          ratio: '16:9',
                          hideControls: false,
                          invertTime: false,
                          toggleInvert: false,
                          debug: false,
                          fullscreen: {
                            enabled: true,
                            fallback: true,
                            iosNative: true
                          },
                          loadSprite: true,
                          iconUrl: 'https://cdn.plyr.io/3.7.8/plyr.svg',
                          blankVideo: 'https://cdn.plyr.io/static/blank.mp4'
                        }}
                      />
                    
                      
                      {video.est_privee && (
                        <div className="private-badge">
                          <FaLock /> Privé
                        </div>
                      )}
                      
                     
                      <button 
                        className="btn-delete-video"
                        onClick={(e) => confirmDeleteItem(video.id, 'videos', e)}
                        title="Supprimer la vidéo"
                      >
                        <FaTrash />
                      </button>
                    </div>

                    <div className="video-info">
                      <div 
                        className="video-user" 
                        onClick={() => navigate(`/profile/${video.user_id}`)}
                      >
                        <img 
                          src={getAvatarUrl(profile?.avatar)} 
                          alt={profile?.pseudo}
                          loading="lazy"
                        />
                        <div>
                          <h4 title={video.titre}>{video.titre}</h4>
                          <p>{profile?.pseudo}</p>
                        </div>
                      </div>

                      <div className="video-stats">
                        <span title="Vues">
                          <FaEye /> {formatNumber(video.vues || 0)}
                        </span>
                        <span title="Likes">
                          <FaHeart className={video.user_liked ? 'liked' : ''} /> 
                          {formatNumber(video.likes || 0)}
                        </span>
                        <span title="Date">
                          {formatDate(video.date_upload)}
                        </span>
                      </div>

                      <div className="video-actions">
                        <button 
                          className={`btn-like ${video.user_liked ? 'liked' : ''}`}
                          onClick={(e) => handleLikeVideo(video.id, e)}
                        >
                          <FaHeart /> {video.user_liked ? 'J\'aime' : 'J\'aime'}
                        </button>
                        <button 
                          className="btn-comment"
                          onClick={() => navigate(`/messages?user=${video.user_id}`)}
                        >
                          <FaComment /> Message
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="no-items">Aucune vidéo pour le moment</p>
              )}
            </div>
          </div>
        )}

     
      </div>

      {/* Modal Upload */}
      <Modal
        isOpen={showUploadModal}
        onClose={() => {
          if (!uploading) {
            setShowUploadModal(false);
            resetUploadForm();
          }
        }}
        title={`Publier une ${uploadType === 'videos' ? 'vidéo' : 'photo'}`}
        size="medium"
      >
        <form onSubmit={handleUpload} className="upload-form">
          {uploadType === 'videos' && (
            <div className="form-group">
              <label>
                Titre <span className="required">*</span>
              </label>
              <input
                type="text"
                name="titre"
                value={uploadData.titre}
                onChange={handleUploadChange}
                placeholder="Titre de votre vidéo"
                maxLength="100"
                required
                disabled={uploading}
              />
            </div>
          )}

          <div className="form-group">
            <label>
              Fichier <span className="required">*</span>
            </label>
            <div className="file-upload-area">
              <input
                type="file"
                ref={fileInputRef}
                id="media-upload"
                accept={uploadType === 'videos' ? "video/mp4,video/quicktime,video/x-msvideo" : "image/jpeg,image/png,image/gif,image/webp"}
                onChange={handleUploadChange}
                disabled={uploading}
                required
              />
              
              <label htmlFor="media-upload" className="file-upload-label">
                {uploadData.file ? (
                  <div className="file-selected">
                    <div className="file-info">
                      <span className="file-name">
                        {uploadData.file.name.length > 30 
                          ? `${uploadData.file.name.substring(0, 27)}...` 
                          : uploadData.file.name}
                      </span>
                      <span className="file-size">
                        {(uploadData.file.size / (1024 * 1024)).toFixed(2)} MB
                      </span>
                    </div>
                    <button 
                      type="button" 
                      className="btn-remove-file"
                      onClick={(e) => {
                        e.preventDefault();
                        setUploadData(prev => ({ ...prev, file: null }));
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      disabled={uploading}
                    >
                      <FaTimes />
                    </button>
                  </div>
                ) : (
                  <div className="file-upload-placeholder">
                    <FaUpload />
                    <span>Cliquez pour sélectionner</span>
                    <small>
                      {uploadType === 'videos' 
                        ? 'MP4, MOV, AVI - Max 100MB' 
                        : 'JPG, PNG, GIF, WEBP - Max 10MB'}
                    </small>
                  </div>
                )}
              </label>
            </div>
          </div>

          {uploadProgress > 0 && (
            <div className="upload-progress">
              <div className="progress-label">
                <span>Upload en cours...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
              </div>
            </div>
          )}

          <div className="form-group checkbox">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isPrivate"
                checked={uploadData.isPrivate}
                onChange={handleUploadChange}
                disabled={uploading}
              />
             <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span className="checkbox-custom">
                  {uploadData.isPrivate ? <FaLock /> : <FaLockOpen />}
                </span>
                <span className="checkbox-text">
                  Privée (visible uniquement par vos matches)
                </span>
              </div>
            </label>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="btn-cancel" 
              onClick={() => {
                setShowUploadModal(false);
                resetUploadForm();
              }}
              disabled={uploading}
            >
              Annuler
            </button>
            <button 
              type="submit" 
              className="btn-submit"
              disabled={uploading || !uploadData.file || (uploadType === 'videos' && !uploadData.titre.trim())}
            >
              {uploading ? (
                <>Publication... {uploadProgress}%</>
              ) : (
                'Publier'
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal Galerie */}
      <Modal
        isOpen={showGalleryModal}
        onClose={() => setShowGalleryModal(false)}
        title={`Mes ${galleryType === 'photos' ? 'photos' : 'vidéos'}`}
        size="large"
      >
        <div className="gallery-modal">
          {loadingGallery ? (
            <Loader />
          ) : (
            <div className="gallery-items">
              {galleryItems.map((item, index) => (
                <div 
                  key={item.id} 
                  className="gallery-item"
                  onClick={() => {
                    if (galleryType === 'photos') {
                      const images = galleryItems.map(i => ({
                        url: `${UPLOADS_URL}/${i.url}`,
                        caption: 'Photo'
                      }));
                      boxview.openBoxview(images, index);
                    } else {
                      setSelectedVideo(item);
                      setShowVideoModal(true);
                    }
                  }}
                >
                  {galleryType === 'photos' ? (
                    <img 
                      src={`${UPLOADS_URL}/${item.url}`} 
                      alt=""
                      loading="lazy"
                    />
                  ) : (
                    <>
                      <img 
                        src={item.thumbnail ? `${UPLOADS_URL}/${item.thumbnail}` : 'https://images.unsplash.com/photo-1574375927938-d5a98e8ffe85'} 
                        alt={item.titre}
                      />
                      <div className="gallery-play-icon">
                        <FaPlay />
                      </div>
                    </>
                  )}
                  
                  {item.est_privee && (
                    <span className="private-icon"><FaLock /></span>
                  )}

                  <button 
                    className="btn-delete-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      confirmDeleteItem(item.id, galleryType, e);
                    }}
                  >
                    <FaTrash />
                  </button>

                  {galleryType === 'videos' && (
                    <div className="item-stats">
                      <span><FaEye /> {item.vues || 0}</span>
                      <span><FaHeart /> {item.likes || 0}</span>
                    </div>
                  )}
                </div>
              ))}

              {galleryItems.length === 0 && (
                <p className="no-items">Aucun élément à afficher</p>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Modal de lecture vidéo avec Plyr */}
      <Modal
        isOpen={showVideoModal}
        onClose={() => {
          setShowVideoModal(false);
          setSelectedVideo(null);
        }}
        title={selectedVideo?.titre?.length > 32 
          ? `${selectedVideo?.titre.substring(0, 29)}...` 
          : selectedVideo?.titre}
        size="large"
      >
        {selectedVideo && showVideoModal && (
          <div className="video-player-container">
            <Plyr
              ref={el => {
                if (el) plyrRefs.current['modal'] = el;
              }}
              source={{
                type: 'video',
                sources: [
                  {
                    src: getVideoUrl(selectedVideo),
                    type: 'video/mp4',
                  }
                ]
              }}
              options={{
                controls: [
                  'play', 'progress', 'current-time', 'duration', 'mute', 'volume',
                  'captions', 'settings', 'pip', 'airplay', 'fullscreen'
                ],
                autoplay: true,
                muted: false,
                volume: 1,
                ratio: '16:9',
                hideControls: false,
                invertTime: false,
                toggleInvert: false,
                debug: false,
                fullscreen: {
                  enabled: true,
                  fallback: true,
                  iosNative: true
                },
                quality: {
                  default: 720,
                  options: [4320, 2880, 2160, 1440, 1080, 720, 576, 480, 360, 240]
                },
                speed: {
                  selected: 1,
                  options: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]
                },
                loadSprite: true,
                iconUrl: 'https://cdn.plyr.io/3.7.8/plyr.svg',
                blankVideo: 'https://cdn.plyr.io/static/blank.mp4'
              }}
            />
          </div>
        )}
      </Modal>

      {/* Modal de confirmation de suppression */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setItemToDelete(null);
        }}
        title="Confirmer la suppression"
        size="small"
      >
        <div className="delete-confirm-content">
          <p>Êtes-vous sûr de vouloir supprimer cette {itemToDelete?.type === 'photos' ? 'photo' : 'vidéo'} ?</p>
          <p className="delete-warning">Cette action est irréversible.</p>
          <div className="delete-confirm-actions">
            <button 
              className="btn-cancel"
              onClick={() => {
                setShowDeleteConfirm(false);
                setItemToDelete(null);
              }}
            >
              Annuler
            </button>
            <button 
              className="btn-delete-confirm"
              onClick={handleDeleteItem}
            >
              <FaTrash /> Supprimer
            </button>
          </div>
        </div>
      </Modal>

      {/* Boxview */}
      <Boxview 
        isOpen={boxview.isOpen}
        onClose={boxview.closeBoxview}
        images={boxview.images}
        initialIndex={boxview.currentIndex}
      />
    </div>
  );
};

export default MyProfile;